import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { Team } from '../../types';
import { teamService } from '../../services/teamService';
import { formatInvitationCode } from '../../utils/invitationUtils';

interface TeamInvitationDialogProps {
  open: boolean;
  onClose: () => void;
  team: Team;
  onCodeRegenerated: (newCode: string) => void;
}

const TeamInvitationDialog: React.FC<TeamInvitationDialogProps> = ({
  open,
  onClose,
  team,
  onCodeRegenerated,
}) => {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleCopyCode = async () => {
    if (!team.invitationCode) {
      setError('No invitation code available');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(team.invitationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const handleRegenerateCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      const newCode = await teamService.regenerateInvitationCode(team.id);
      onCodeRegenerated(newCode);
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate code');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!team.invitationCode) {
      setError('No invitation code available');
      return;
    }

    const shareData = {
      title: `Join ${team.name} on Waka Running`,
      text: `Join my team "${team.name}" on Waka Running! Use code: ${team.invitationCode}`,
      url: window.location.origin,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback to copying
        await navigator.clipboard.writeText(
          `Join my team "${team.name}" on Waka Running! Use code: ${team.invitationCode}`
        );
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      setError('Failed to share');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon />
          Invite Athletes to {team.name}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Share this invitation code with athletes to let them join your team:
          </Typography>
          
          {!team.invitationCode ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                This team doesn't have an invitation code yet. Click "Generate Code" to create one.
              </Typography>
            </Alert>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mt: 2,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography 
                variant="h4" 
                component="span" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  letterSpacing: 2,
                  color: 'primary.main'
                }}
              >
                {formatInvitationCode(team.invitationCode)}
              </Typography>
              
              <IconButton 
                onClick={handleCopyCode}
                color="primary"
                disabled={copied}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Box>
          )}
          
          {copied && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Code copied to clipboard!
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Instructions for Athletes:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            1. Open the Waka Running app
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            2. Go to the "Teams" page
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            3. Click "Join Team" and enter the code: <strong>{team.invitationCode || 'Generate code first'}</strong>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleCopyCode}
            disabled={copied || !team.invitationCode}
          >
            {copied ? 'Copied!' : 'Copy Code'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShare}
            disabled={!team.invitationCode}
          >
            Share
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRegenerateCode}
            disabled={loading}
            color="warning"
          >
            {loading ? <CircularProgress size={20} /> : (team.invitationCode ? 'New Code' : 'Generate Code')}
          </Button>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Regenerating a new code will invalidate the old one. 
            Make sure to share the new code with your athletes.
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamInvitationDialog;
